import { useState, useMemo, useEffect, useRef, useCallback } from 'react';

import { Box, Tabs, Tab, Typography, Paper, Button, Chip, Divider, TextField, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl } from '@mui/material';
import { InfoOutlined, Add, Delete, Timeline, Chat, AccountTree } from '@mui/icons-material';

import { BEHAVIORAL_CODES } from '../constants/playerConfig';

const QUERY_INTENT_LABELS = [
  { id: 'high_delegation', label: 'High Delegation', description: 'Asking the AI to solve, write, or fix without demonstrating specific engagement. "How do I solve this?" "Fix my code." "Write the function."' },
  { id: 'targeted', label: 'Targeted', description: 'Asking about a specific error, concept, or approach already attempted. "Why does my loop skip the last character?" "I tried X but got Y."' },
  { id: 'follow_up', label: 'Follow-up', description: 'Continuing or clarifying the previous exchange without a new substantive request. "Ok." "Not sure what you mean." "Can you explain that differently?"' },
  { id: 'task_clarification', label: 'Task Clarification', description: 'Asking about assignment requirements, not code. "What does the task want me to do?" "Do I need to handle uppercase?"' },
  { id: 'validation', label: 'Validation', description: 'Asking if existing code is correct. "Is this right?" "Does this look good?"' },
  { id: 'conceptual', label: 'Conceptual', description: 'Asking about a programming concept without reference to their specific code or task. "What does .strip() do?" "How do lists work?"' },
];

const W1_PROFILES = [
  { id: 'cold_start', label: 'Cold Start', color: '#94a3b8', description: 'First query with no prior attempt.' },
  { id: 'oriented', label: 'Oriented', color: '#3b82f6', description: 'Made a genuine attempt — moderate code and effort before asking.' },
  { id: 'struggling', label: 'Struggling', color: '#ef4444', description: 'Extensive effort but no resolution — high runs, errors, edits before asking.' },
];

const W2_PROFILES = [
  { id: 'passive', label: 'Passive', color: '#94a3b8', description: 'Zero new effort since last AI response. Re-queries immediately.' },
  { id: 'iterating', label: 'Iterating', color: '#3b82f6', description: 'Light effort, some edits, low error count. Steady progress.' },
  { id: 'debugging', label: 'Debugging', color: '#f59e0b', description: 'Multiple runs and errors.' },
  { id: 'spinning', label: 'Spinning', color: '#ef4444', description: 'High thinking and edits but barely tests. Writing without executing.' },
];

export default function PlayerSidebar({
  activeTab, setActiveTab,
  activeRecording, taggedBlocks,
  handleStartRecording, handleStopRecording,
  handleQuickTagOpen, handleManualTagOpen,
  handleDeleteBlock, handleBlockClick, formatTime,
  timelineSelection, setTimelineSelection,
  savedPatterns, onSavePattern, onDeletePattern,
  // LLM Queries props
  chatQueries = [],
  queryLabels = {},
  onUpdateQueryLabel,
  onSeekToTime,
  // Profile labeling props
  profileLabels = {},
  onUpdateProfileLabel,
}) {
  
  // State for adding new labels
  const [addLabelDialogOpen, setAddLabelDialogOpen] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelDescription, setNewLabelDescription] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#3b82f6');
  const [customBehavioralCodes, setCustomBehavioralCodes] = useState([]);

  // State for pattern naming
  const [patternName, setPatternName] = useState('');
  const [patternDescription, setPatternDescription] = useState('');
  
  // State for UI controls
  const [behaviorMakeupExpanded, setBehaviorMakeupExpanded] = useState(true);
  const [editingPatternId, setEditingPatternId] = useState(null);
  const [editingPatternDbId, setEditingPatternDbId] = useState(null);
  
  // State for Pattern Assessment (Tab 2 & 3)
  const [productivity, setProductivity] = useState(null);
  const [interventionRecommendation, setInterventionRecommendation] = useState('');
  const [annotatorConfidence, setAnnotatorConfidence] = useState(null);

  const [preQueryPatternName, setPreQueryPatternName] = useState('Pre-Query Behavior');
  const [preQueryPatternDescription, setPreQueryPatternDescription] = useState('');
  const [preQueryProductivity, setPreQueryProductivity] = useState(null);
  const [preQueryIntervention, setPreQueryIntervention] = useState('');
  const [preQueryConfidence, setPreQueryConfidence] = useState(null);
  const [preQueryEditingId, setPreQueryEditingId] = useState(null);
  const [showPreQuerySaved, setShowPreQuerySaved] = useState(false);

  // State for selected query
  const [selectedQueryId, setSelectedQueryId] = useState(null);

  // State for selected profile window
  const [selectedProfileQueryId, setSelectedProfileQueryId] = useState(null);

  // ============================================
  // PRE-QUERY SUB-SELECTION STATE
  // ============================================
  const [preQuerySubSelection, setPreQuerySubSelection] = useState(null); // { startTime, endTime }
  const [isDraggingSubSelection, setIsDraggingSubSelection] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const subSelectionBarRef = useRef(null);

  // ============================================
  // PRE-QUERY DETECTION
  // ============================================
  
  // Find the first "Seeking Help" behavioral tag (first time annotator marked help-seeking)
  const firstSeekingHelpTime = useMemo(() => {
    if (!taggedBlocks || taggedBlocks.length === 0) return null;
    
    const seekingHelpTags = taggedBlocks.filter(block => 
      block.behavior?.id === 'seekingHelp'
    );
    
    if (seekingHelpTags.length === 0) return null;
    
    const sortedTags = [...seekingHelpTags].sort((a, b) => a.startTime - b.startTime);
    return sortedTags[0].startTime;
  }, [taggedBlocks]);

  // Calculate the pre-query time range
  const preQueryTimeRange = useMemo(() => {
    if (firstSeekingHelpTime === null) return null;
    
    const startTime = 0;
    const endTime = firstSeekingHelpTime;
    
    if (endTime <= startTime) return null;
    
    return { startTime, endTime };
  }, [firstSeekingHelpTime]);

  // Get behaviors within the pre-query range
  const preQueryBehaviors = useMemo(() => {
    if (!preQueryTimeRange || !taggedBlocks || taggedBlocks.length === 0) return [];
    
    const { startTime, endTime } = preQueryTimeRange;
    
    return taggedBlocks.filter(block => {
      return block.startTime < endTime && block.endTime > startTime;
    }).map(block => ({
      ...block,
      overlapStart: Math.max(block.startTime, startTime),
      overlapEnd: Math.min(block.endTime, endTime)
    }));
  }, [preQueryTimeRange, taggedBlocks]);

  // Aggregate behavior summary for pre-query
  const preQueryBehaviorSummary = useMemo(() => {
    if (preQueryBehaviors.length === 0) return [];
    
    const summary = {};
    preQueryBehaviors.forEach(block => {
      const duration = block.overlapEnd - block.overlapStart;
      if (!summary[block.behavior.id]) {
        summary[block.behavior.id] = {
          behavior: block.behavior,
          totalDuration: 0,
          count: 0,
          earliestStart: block.overlapStart
        };
      }
      summary[block.behavior.id].totalDuration += duration;
      summary[block.behavior.id].count += 1;
      if (block.overlapStart < summary[block.behavior.id].earliestStart) {
        summary[block.behavior.id].earliestStart = block.overlapStart;
      }
    });
    
    return Object.values(summary).sort((a, b) => a.earliestStart - b.earliestStart);
  }, [preQueryBehaviors]);

  // ============================================
  // QUERY WINDOWS FOR PROFILE LABELING
  // ============================================

  const queryWindows = useMemo(() => {
    if (!chatQueries || chatQueries.length === 0) return [];

    const sorted = [...chatQueries].sort((a, b) => a.time - b.time);

    return sorted.map((query, index) => {
      const isFirst = index === 0;
      const windowStart = isFirst ? 0 : sorted[index - 1].time;
      const windowEnd = query.time;
      const windowDuration = windowEnd - windowStart;

      // Find behaviors within this window
      const behaviorsInWindow = (taggedBlocks || [])
        .filter(block => block.startTime < windowEnd && block.endTime > windowStart)
        .map(block => ({
          ...block,
          overlapStart: Math.max(block.startTime, windowStart),
          overlapEnd: Math.min(block.endTime, windowEnd)
        }))
        .sort((a, b) => a.overlapStart - b.overlapStart);

      // Aggregate behavior summary
      const summaryMap = {};
      behaviorsInWindow.forEach(block => {
        const duration = block.overlapEnd - block.overlapStart;
        const id = block.behavior.id;
        if (!summaryMap[id]) {
          summaryMap[id] = {
            behavior: block.behavior,
            totalDuration: 0,
            count: 0,
            earliestStart: block.overlapStart
          };
        }
        summaryMap[id].totalDuration += duration;
        summaryMap[id].count += 1;
        if (block.overlapStart < summaryMap[id].earliestStart) {
          summaryMap[id].earliestStart = block.overlapStart;
        }
      });
      const behaviorSummary = Object.values(summaryMap).sort((a, b) => a.earliestStart - b.earliestStart);

      return {
        queryId: query.id,
        queryIndex: index,
        queryText: query.text || query.payload?.query || 'No query text',
        queryTime: query.time,
        windowType: isFirst ? 'W1' : 'W2',
        windowStart,
        windowEnd,
        windowDuration,
        behaviors: behaviorsInWindow,
        behaviorSummary,
        profiles: isFirst ? W1_PROFILES : W2_PROFILES,
      };
    });
  }, [chatQueries, taggedBlocks]);

  // Count labeled profiles
  const labeledProfileCount = useMemo(() => {
    return Object.keys(profileLabels).length;
  }, [profileLabels]);

  // ============================================
  // PRE-QUERY SUB-SELECTION COMPUTED VALUES
  // ============================================

  // Convert pixel position to time within pre-query range
  const pixelToTime = useCallback((pixelX) => {
    if (!subSelectionBarRef.current || !preQueryTimeRange) return 0;
    const rect = subSelectionBarRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (pixelX - rect.left) / rect.width));
    const totalDuration = preQueryTimeRange.endTime - preQueryTimeRange.startTime;
    return preQueryTimeRange.startTime + ratio * totalDuration;
  }, [preQueryTimeRange]);

  // Convert time to percentage within pre-query range
  const timeToPercent = useCallback((time) => {
    if (!preQueryTimeRange) return 0;
    const totalDuration = preQueryTimeRange.endTime - preQueryTimeRange.startTime;
    if (totalDuration === 0) return 0;
    return ((time - preQueryTimeRange.startTime) / totalDuration) * 100;
  }, [preQueryTimeRange]);

  // Mouse down on the behavior bar to start a new sub-selection
  const handleSubSelectionMouseDown = useCallback((e) => {
    const time = pixelToTime(e.clientX);
    setDragStart(time);
    setDragCurrent(time);
    setIsDraggingSubSelection(true);
    setPreQuerySubSelection(null);
    e.preventDefault();
  }, [pixelToTime]);

  // Global mouse move for new selection dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingSubSelection && dragStart !== null) {
        const time = pixelToTime(e.clientX);
        setDragCurrent(time);
      }
    };

    const handleMouseUp = () => {
      if (isDraggingSubSelection && dragStart !== null && dragCurrent !== null) {
        const start = Math.min(dragStart, dragCurrent);
        const end = Math.max(dragStart, dragCurrent);
        
        if (end - start > 0.5) {
          const clampedStart = Math.max(preQueryTimeRange?.startTime ?? 0, start);
          const clampedEnd = Math.min(preQueryTimeRange?.endTime ?? end, end);
          setPreQuerySubSelection({ startTime: clampedStart, endTime: clampedEnd });
        }
      }
      
      setIsDraggingSubSelection(false);
      setDragStart(null);
      setDragCurrent(null);
    };

    if (isDraggingSubSelection) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingSubSelection, dragStart, dragCurrent, pixelToTime, preQueryTimeRange]);

  // Sync sub-selection to timeline
  useEffect(() => {
    if (activeTab === 1 && preQuerySubSelection) {
      setTimelineSelection({
        startTime: preQuerySubSelection.startTime,
        endTime: preQuerySubSelection.endTime,
        isPreQuery: true,
        isSubSelection: true
      });
    } else if (activeTab === 1 && preQueryTimeRange && !preQuerySubSelection) {
      setTimelineSelection({
        startTime: preQueryTimeRange.startTime,
        endTime: preQueryTimeRange.endTime,
        isPreQuery: true
      });
    }
  }, [activeTab, preQuerySubSelection, preQueryTimeRange, setTimelineSelection]);

  // Clear timeline highlight when leaving pre-query tab (but keep sub-selection)
  useEffect(() => {
    if (activeTab !== 1) {
      setTimelineSelection(prev => {
        if (prev?.isPreQuery) return null;
        return prev;
      });
    }
  }, [activeTab, setTimelineSelection]);

  // Check if a pre-query pattern already exists
  const existingPreQueryPattern = useMemo(() => {
    return savedPatterns?.find(p => p.isPreQuery === true);
  }, [savedPatterns]);

  // Load existing pre-query pattern data when tab is selected
  useEffect(() => {
    if (activeTab === 1 && existingPreQueryPattern) {
      setPreQueryPatternName(existingPreQueryPattern.name);
      setPreQueryPatternDescription(existingPreQueryPattern.description || '');
      setPreQueryEditingId(existingPreQueryPattern.id);
      if (existingPreQueryPattern.assessment) {
        setPreQueryProductivity(existingPreQueryPattern.assessment.productivity);
        setPreQueryIntervention(existingPreQueryPattern.assessment.interventionRecommendation || '');
        setPreQueryConfidence(existingPreQueryPattern.assessment.annotatorConfidence);
      }
    }
  }, [activeTab, existingPreQueryPattern]);

  // Hide pre-query saved message after 3 seconds
  useEffect(() => {
    if (showPreQuerySaved) {
      const timer = setTimeout(() => {
        setShowPreQuerySaved(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showPreQuerySaved]);

  // ============================================
  // EXISTING PATTERN LOGIC
  // ============================================

  // Calculate behaviors within the current selection
  const behaviorsInSelection = useMemo(() => {
    if (!timelineSelection || !taggedBlocks || taggedBlocks.length === 0) return [];
    
    const { startTime, endTime } = timelineSelection;
    
    return taggedBlocks.filter(block => {
      const blockStart = block.startTime;
      const blockEnd = block.endTime;
      return blockStart < endTime && blockEnd > startTime;
    }).map(block => ({
      ...block,
      overlapStart: Math.max(block.startTime, startTime),
      overlapEnd: Math.min(block.endTime, endTime)
    }));
  }, [timelineSelection, taggedBlocks]);

  // Aggregate behavior summary for selection
  const behaviorSummary = useMemo(() => {
    if (behaviorsInSelection.length === 0) return [];
    
    const summary = {};
    behaviorsInSelection.forEach(block => {
      const duration = block.overlapEnd - block.overlapStart;
      if (!summary[block.behavior.id]) {
        summary[block.behavior.id] = {
          behavior: block.behavior,
          totalDuration: 0,
          count: 0,
          earliestStart: block.overlapStart
        };
      }
      summary[block.behavior.id].totalDuration += duration;
      summary[block.behavior.id].count += 1;
      if (block.overlapStart < summary[block.behavior.id].earliestStart) {
        summary[block.behavior.id].earliestStart = block.overlapStart;
      }
    });
    
    return Object.values(summary).sort((a, b) => a.earliestStart - b.earliestStart);
  }, [behaviorsInSelection]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleAddLabelClick = () => {
    setAddLabelDialogOpen(true);
  };

  const handleAddLabelConfirm = () => {
    if (!newLabelName.trim()) return;

    const newCode = {
      id: `custom_${Date.now()}`,
      label: newLabelName,
      description: newLabelDescription,
      color: newLabelColor,
      isCustom: true
    };

    setCustomBehavioralCodes([...customBehavioralCodes, newCode]);
    setNewLabelName('');
    setNewLabelDescription('');
    setNewLabelColor('#3b82f6');
    setAddLabelDialogOpen(false);
  };

  const handleAddLabelCancel = () => {
    setNewLabelName('');
    setNewLabelDescription('');
    setNewLabelColor('#3b82f6');
    setAddLabelDialogOpen(false);
  };

  const handleSavePattern = () => {
    if (!timelineSelection || !patternName.trim()) return;
    
    const editingPattern = savedPatterns.find(p => p.id === editingPatternId);
    const isPreQuery = editingPattern?.isPreQuery || false;
    
    const patternData = {
      id: editingPatternId || Date.now(),
      dbId: editingPatternDbId,
      name: patternName,
      description: patternDescription,
      startTime: timelineSelection.startTime,
      endTime: timelineSelection.endTime,
      color: isPreQuery ? '#8b5cf6' : '#f59e0b',
      behaviors: behaviorsInSelection.map(b => ({
        id: b.id,
        dbId: b.dbId,
        behaviorId: b.behavior.id,
        behaviorLabel: b.behavior.label,
        behaviorColor: b.behavior.color,
        startTime: b.startTime,
        endTime: b.endTime,
        overlapStart: b.overlapStart,
        overlapEnd: b.overlapEnd
      })),
      behaviorSummary: behaviorSummary.map(s => ({
        behaviorId: s.behavior.id,
        behaviorLabel: s.behavior.label,
        behaviorColor: s.behavior.color,
        totalDuration: s.totalDuration,
        count: s.count,
        earliestStart: s.earliestStart
      })),
      assessment: {
        productivity,
        interventionRecommendation,
        annotatorConfidence,
        isComplete: !!(productivity && interventionRecommendation.trim() && annotatorConfidence)
      },
      isPreQuery: isPreQuery
    };

    if (editingPatternId) {
      onSavePattern(patternData, true);
    } else {
      onSavePattern(patternData);
    }

    setPatternName('');
    setPatternDescription('');
    setTimelineSelection(null);
    setEditingPatternId(null);
    setEditingPatternDbId(null);
    setProductivity(null);
    setInterventionRecommendation('');
    setAnnotatorConfidence(null);
  };

  const handleSavePreQueryPattern = () => {
    if (!preQueryTimeRange || !preQueryPatternName.trim()) return;
    
    const patternData = {
      id: preQueryEditingId || Date.now(),
      dbId: existingPreQueryPattern?.dbId,
      name: preQueryPatternName,
      description: preQueryPatternDescription,
      startTime: preQueryTimeRange.startTime,
      endTime: preQueryTimeRange.endTime,
      color: '#8b5cf6',
      behaviors: preQueryBehaviors.map(b => ({
        id: b.id,
        dbId: b.dbId,
        behaviorId: b.behavior.id,
        behaviorLabel: b.behavior.label,
        behaviorColor: b.behavior.color,
        startTime: b.startTime,
        endTime: b.endTime,
        overlapStart: b.overlapStart,
        overlapEnd: b.overlapEnd
      })),
      behaviorSummary: preQueryBehaviorSummary.map(s => ({
        behaviorId: s.behavior.id,
        behaviorLabel: s.behavior.label,
        behaviorColor: s.behavior.color,
        totalDuration: s.totalDuration,
        count: s.count,
        earliestStart: s.earliestStart
      })),
      assessment: {
        productivity: preQueryProductivity,
        interventionRecommendation: preQueryIntervention,
        annotatorConfidence: preQueryConfidence,
        isComplete: !!(preQueryProductivity && preQueryIntervention.trim() && preQueryConfidence)
      },
      isPreQuery: true
    };

    if (preQueryEditingId || existingPreQueryPattern) {
      onSavePattern(patternData, true);
    } else {
      onSavePattern(patternData);
    }

    setPreQueryEditingId(patternData.id);
    setShowPreQuerySaved(true);
  };

  const handleLoadPattern = (pattern) => {
    setTimelineSelection({
      startTime: pattern.startTime,
      endTime: pattern.endTime
    });
    setPatternName(pattern.name);
    setPatternDescription(pattern.description || '');
    setEditingPatternId(pattern.id);
    setEditingPatternDbId(pattern.dbId);
    setBehaviorMakeupExpanded(true);
    
    if (pattern.assessment) {
      setProductivity(pattern.assessment.productivity);
      setInterventionRecommendation(pattern.assessment.interventionRecommendation || '');
      setAnnotatorConfidence(pattern.assessment.annotatorConfidence);
    } else {
      setProductivity(null);
      setInterventionRecommendation('');
      setAnnotatorConfidence(null);
    }
  };

  const handleClearSelection = () => {
    setTimelineSelection(null);
    setPatternName('');
    setPatternDescription('');
    setEditingPatternId(null);
    setEditingPatternDbId(null);
    setProductivity(null);
    setInterventionRecommendation('');
    setAnnotatorConfidence(null);
  };

  // Handle query selection and seek
  const handleQueryClick = (query) => {
    setSelectedQueryId(query.id);
    if (onSeekToTime) {
      onSeekToTime(query.time);
    }
  };

  // Handle query label change
  const handleQueryLabelChange = (queryId, labelId) => {
    if (onUpdateQueryLabel) {
      onUpdateQueryLabel(queryId, labelId);
    }
  };

  // Handle profile window click — seek and highlight the window
  const handleProfileWindowClick = (window) => {
    setSelectedProfileQueryId(window.queryId);
    if (onSeekToTime) {
      onSeekToTime(window.windowStart);
    }
    setTimelineSelection({
      startTime: window.windowStart,
      endTime: window.windowEnd,
      isProfileWindow: true
    });
  };

  // Handle profile label change
  const handleProfileLabelChange = (queryId, profileId) => {
    if (onUpdateProfileLabel) {
      onUpdateProfileLabel(queryId, profileId);
    }
  };

  // Clear profile timeline highlight when leaving profiles tab
  useEffect(() => {
    if (activeTab !== 3) {
      setTimelineSelection(prev => {
        if (prev?.isProfileWindow) return null;
        return prev;
      });
    }
  }, [activeTab, setTimelineSelection]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderLabelingSection = (title, codes, customCodes) => (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" fontWeight="700" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Tooltip title="Add custom label" arrow>
          <IconButton 
            size="small" 
            onClick={handleAddLabelClick}
            sx={{ p: 0.5, color: '#6b7280', '&:hover': { color: '#3b82f6', bgcolor: '#eff6ff' } }}
          >
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {[...codes, ...customCodes].map((code) => {
          const isRecording = activeRecording?.behavior.id === code.id;
          return (
            <Paper 
              key={code.id}
              elevation={0} 
              sx={{ 
                border: isRecording ? `2px solid ${code.color}` : '1px solid #e5e7eb', 
                borderRadius: 1.5, p: 1, transition: '0.2s',
                bgcolor: isRecording ? `${code.color}10` : 'transparent',
                '&:hover': { borderColor: code.color, bgcolor: '#f9fafb' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: code.color, ...(isRecording && { animation: 'pulse 1s infinite' }) }} />
                  <Typography variant="body2" fontWeight="500" sx={{ color: '#111827' }}>
                    {code.label}
                    {code.isCustom && <Chip label="Custom" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: '#e0e7ff', color: '#4f46e5' }} />}
                  </Typography>
                  {code.description && (
                    <Tooltip title={code.description} arrow placement="top">
                      <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                        <InfoOutlined sx={{ fontSize: 14, color: '#9ca3af' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {isRecording ? (
                  <Button size="small" variant="contained" onClick={handleStopRecording} sx={{ bgcolor: '#dc2626', fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0 }}>Stop</Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" variant="outlined" onClick={() => handleStartRecording(code)} disabled={!!activeRecording} sx={{ borderColor: '#e5e7eb', color: '#374151', fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0 }}>Rec</Button>
                    <Button size="small" variant="outlined" onClick={() => handleQuickTagOpen(code)} disabled={!!activeRecording} sx={{ borderColor: '#e5e7eb', color: '#374151', fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0 }}>Quick</Button>
                    <Button size="small" variant="outlined" onClick={() => handleManualTagOpen(code)} disabled={!!activeRecording} sx={{ borderColor: '#e5e7eb', color: '#374151', fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 0 }}>Manual</Button>
                  </Box>
                )}
              </Box>
              {code.description && <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', ml: 0.5, mt: 0.5 }}>{code.description}</Typography>}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );

  const renderBehaviorBar = (summary, totalDuration) => {
    if (!summary || summary.length === 0) return null;
    
    const sortedSummary = [...summary].sort((a, b) => {
      const aStart = a.earliestStart ?? a.behavior?.earliestStart ?? 0;
      const bStart = b.earliestStart ?? b.behavior?.earliestStart ?? 0;
      return aStart - bStart;
    });
    
    return (
      <Box sx={{ 
        display: 'flex', 
        height: 8, 
        borderRadius: 1, 
        overflow: 'hidden',
        bgcolor: '#e5e7eb'
      }}>
        {sortedSummary.map((item, index) => (
          <Tooltip 
            key={index} 
            title={`${item.behaviorLabel || item.behavior?.label}: ${formatTime(item.totalDuration)}`}
            arrow
          >
            <Box 
              sx={{ 
                width: `${(item.totalDuration / totalDuration) * 100}%`,
                bgcolor: item.behaviorColor || item.behavior?.color,
                minWidth: 4,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }} 
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  // Render a mini behavioral sequence bar for profile windows
  const renderWindowSequenceBar = (behaviors, windowDuration) => {
    if (!behaviors || behaviors.length === 0 || windowDuration <= 0) {
      return (
        <Box sx={{ height: 20, borderRadius: 1, bgcolor: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>No behavioral tags</Typography>
        </Box>
      );
    }

    const sorted = [...behaviors].sort((a, b) => a.overlapStart - b.overlapStart);

    return (
      <Box sx={{ height: 20, borderRadius: 1, overflow: 'hidden', bgcolor: '#e5e7eb', display: 'flex', position: 'relative' }}>
        {sorted.map((block, index) => {
          const duration = block.overlapEnd - block.overlapStart;
          const width = (duration / windowDuration) * 100;
          return (
            <Tooltip
              key={index}
              title={`${block.behavior.label}: ${formatTime(duration)}`}
              arrow
            >
              <Box sx={{
                width: `${Math.max(width, 0.5)}%`,
                height: '100%',
                bgcolor: block.behavior.color,
                minWidth: 2,
                '&:hover': { opacity: 0.8 }
              }} />
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  // Render the interactive pre-query behavior bar with sub-selection support
  const renderInteractivePreQueryBar = () => {
    if (!preQueryTimeRange || preQueryBehaviors.length === 0) return null;

    const totalDuration = preQueryTimeRange.endTime - preQueryTimeRange.startTime;
    const sortedBehaviors = [...preQueryBehaviors].sort((a, b) => a.overlapStart - b.overlapStart);

    let selLeft = 0;
    let selWidth = 0;
    if (preQuerySubSelection) {
      selLeft = timeToPercent(preQuerySubSelection.startTime);
      const selRight = timeToPercent(preQuerySubSelection.endTime);
      selWidth = selRight - selLeft;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" fontWeight="600" sx={{ color: '#1d4ed8', textTransform: 'uppercase', mb: 1, display: 'block' }}>
          Behavioral Makeup
        </Typography>
        
        <Box 
          ref={subSelectionBarRef}
          onMouseDown={handleSubSelectionMouseDown}
          sx={{ 
            position: 'relative',
            height: 28,
            borderRadius: 1.5,
            overflow: 'visible',
            cursor: 'crosshair',
            userSelect: 'none',
            border: '1px solid #d1d5db',
            bgcolor: '#e5e7eb'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            height: '100%',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1
          }}>
            {sortedBehaviors.map((block, index) => {
              const duration = block.overlapEnd - block.overlapStart;
              return (
                <Tooltip 
                  key={index} 
                  title={`${block.behavior.label}: ${formatTime(duration)}`}
                  arrow
                >
                  <Box 
                    sx={{ 
                      width: `${(duration / totalDuration) * 100}%`,
                      bgcolor: block.behavior.color,
                      minWidth: 2,
                      height: '100%',
                      cursor: 'crosshair'
                    }} 
                  />
                </Tooltip>
              );
            })}
          </Box>

          {isDraggingSubSelection && dragStart !== null && dragCurrent !== null && (() => {
            const start = Math.min(dragStart, dragCurrent);
            const end = Math.max(dragStart, dragCurrent);
            const left = timeToPercent(Math.max(start, preQueryTimeRange.startTime));
            const right = timeToPercent(Math.min(end, preQueryTimeRange.endTime));
            const width = right - left;
            if (width <= 0) return null;
            return (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: `${left}%`,
                width: `${width}%`,
                height: '100%',
                bgcolor: 'rgba(59, 130, 246, 0.08)',
                border: '1.5px dashed rgba(59, 130, 246, 0.5)',
                borderRadius: 1,
                zIndex: 3,
                pointerEvents: 'none',
                boxSizing: 'border-box'
              }} />
            );
          })()}

          {preQuerySubSelection && !isDraggingSubSelection && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: `${selLeft}%`,
              width: `${selWidth}%`,
              height: '100%',
              bgcolor: '#f59e0b',
              opacity: 0.6,
              borderRadius: 1,
              zIndex: 3,
              pointerEvents: 'none',
              border: '2px solid #fbbf24',
              boxSizing: 'border-box'
            }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>
            {formatTime(preQueryTimeRange.startTime)}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>
            {formatTime(preQueryTimeRange.endTime)}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Render Assessment Form (shared between Patterns and Pre-Query)
  const renderAssessmentForm = (
    productivityVal, setProductivityVal,
    interventionVal, setInterventionVal,
    confidenceVal, setConfidenceVal
  ) => {
    const isComplete = !!(productivityVal && interventionVal.trim() && confidenceVal);
    const progress = [productivityVal, interventionVal.trim(), confidenceVal].filter(Boolean).length;

    return (
      <>
        {/* Productivity */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
              Productivity
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {productivityVal ? ['Unproductive', 'Low', 'Neutral', 'Good', 'Excellent'][productivityVal - 1] : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[1, 2, 3, 4, 5].map((v) => (
              <Box
                key={v}
                onClick={() => setProductivityVal(v)}
                sx={{
                  flex: 1, 
                  height: 40, 
                  borderRadius: 1.5, 
                  cursor: 'pointer',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: productivityVal === v ? '#3b82f6' : '#f9fafb',
                  border: productivityVal === v ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  color: productivityVal === v ? '#ffffff' : '#6b7280',
                  fontWeight: productivityVal === v ? 700 : 500,
                  fontSize: '0.95rem',
                  transition: 'all 0.15s',
                  '&:hover': { 
                    bgcolor: productivityVal === v ? '#2563eb' : '#e5e7eb',
                    borderColor: productivityVal === v ? '#1d4ed8' : '#d1d5db'
                  }
                }}
              >
                {v}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Spinning wheels</Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Learning effectively</Typography>
          </Box>
        </Box>

        {/* Intervention Recommendation */}
        <Box>
          <Typography variant="body2" fontWeight="600" sx={{ color: '#374151', mb: 1 }}>
            How would you recommend intervening with this student?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={interventionVal}
            onChange={(e) => setInterventionVal(e.target.value)}
            placeholder="Describe the intervention strategy you would recommend..."
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 2,
                bgcolor: '#f9fafb',
                '&:hover': { bgcolor: '#fff' },
                '&.Mui-focused': { bgcolor: '#fff' }
              }
            }}
          />
        </Box>

        {/* Annotator Confidence */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
              Your Confidence
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {confidenceVal ? ['Guessing', 'Unsure', 'Moderate', 'Confident', 'Certain'][confidenceVal - 1] : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[1, 2, 3, 4, 5].map((v) => (
              <Box
                key={v}
                onClick={() => setConfidenceVal(v)}
                sx={{
                  flex: 1, 
                  height: 40, 
                  borderRadius: 1.5, 
                  cursor: 'pointer',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: confidenceVal === v ? '#3b82f6' : '#f9fafb',
                  border: confidenceVal === v ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  color: confidenceVal === v ? '#ffffff' : '#6b7280',
                  fontWeight: confidenceVal === v ? 700 : 500,
                  fontSize: '0.95rem',
                  transition: 'all 0.15s',
                  '&:hover': { 
                    bgcolor: confidenceVal === v ? '#2563eb' : '#e5e7eb',
                    borderColor: confidenceVal === v ? '#1d4ed8' : '#d1d5db'
                  }
                }}
              >
                {v}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Guessing</Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Certain</Typography>
          </Box>
        </Box>

        {/* Completion Status */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 1.5,
          bgcolor: isComplete ? '#dcfce7' : '#f9fafb',
          borderRadius: 2,
          border: isComplete ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
          transition: 'all 0.2s'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 10, 
              height: 10, 
              borderRadius: '50%', 
              bgcolor: isComplete ? '#22c55e' : '#d1d5db'
            }} />
            <Typography variant="body2" sx={{ 
              color: isComplete ? '#166534' : '#6b7280',
              fontWeight: isComplete ? 600 : 400
            }}>
              {isComplete 
                ? 'Assessment complete' 
                : `${progress} of 3 required`}
            </Typography>
          </Box>
        </Box>
      </>
    );
  };

  const isAssessmentComplete = !!(productivity && interventionRecommendation.trim() && annotatorConfidence);
  const isPreQueryAssessmentComplete = !!(preQueryProductivity && preQueryIntervention.trim() && preQueryConfidence);

  // Filter out pre-query patterns from regular patterns list
  const regularPatterns = savedPatterns?.filter(p => !p.isPreQuery) || [];

  // Count labeled queries
  const labeledQueryCount = Object.keys(queryLabels).length;

  return (
    <Box sx={{ bgcolor: '#ffffff', borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Tabs 
        value={activeTab} 
        onChange={(e, v) => setActiveTab(v)} 
        sx={{ borderBottom: '1px solid #e5e7eb', minHeight: 42, flexShrink: 0 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Labeling" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem' }} />
        <Tab label="Pre-Query" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem' }} />
        <Tab label="LLM-Queries" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem' }} />
        <Tab label="Profiles" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem' }} />
        <Tab label="Assessment" sx={{ minWidth: 0, flex: 1, fontSize: '0.75rem' }} />
      </Tabs>

      {/* Tab 0: Behavioral Labeling */}
      {activeTab === 0 && (
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {renderLabelingSection("Behavioral Labeling", BEHAVIORAL_CODES, customBehavioralCodes)}
          <Divider />
          
          <Box sx={{ p: 1.5, bgcolor: '#f9fafb', flex: '0 0 auto' }}>
            <Typography variant="caption" fontWeight="700" sx={{ color: '#6b7280', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Session Tags ({taggedBlocks.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 150, overflowY: 'auto' }}>
              {taggedBlocks.map((block) => (
                <Box
                  key={block.id}
                  onClick={() => handleBlockClick(block)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    p: 0.75,
                    borderRadius: 1.5,
                    bgcolor: `${block.behavior.color}10`,
                    border: `1px solid ${block.behavior.color}30`,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: `${block.behavior.color}20` }
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: block.behavior.color, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: block.behavior.color, fontWeight: 600, display: 'block', lineHeight: 1.2 }}>
                      {block.behavior.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                      {formatTime(block.startTime)} - {formatTime(block.endTime)}
                    </Typography>
                  </Box>
                  {block.confidence && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.5, py: 0.25, borderRadius: 1, bgcolor: '#f3f4f6', border: '1px solid #e5e7eb' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#6b7280', fontWeight: 500 }}>
                        C:{block.confidence}
                      </Typography>
                    </Box>
                  )}
                  <IconButton 
                    size="small" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteBlock(block.id); }}
                    sx={{ p: 0.25, color: '#9ca3af', '&:hover': { color: '#ef4444', bgcolor: '#fef2f2' } }}
                  >
                    <Delete sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab 1: Pre-Query */}
      {activeTab === 1 && (
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
            {preQueryTimeRange ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper elevation={0} sx={{ p: 2, border: '2px solid #3b82f6', borderRadius: 2, bgcolor: '#eff6ff' }}>
                  <Typography variant="caption" fontWeight="600" sx={{ color: '#1d4ed8', textTransform: 'uppercase' }}>
                    {existingPreQueryPattern ? 'Editing Pre-Query Pattern' : 'Pre-Query Region'}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#111827', mt: 0.5 }}>
                    {formatTime(preQueryTimeRange.startTime)} → {formatTime(preQueryTimeRange.endTime)}
                  </Typography>
                  
                  {renderInteractivePreQueryBar()}

                  {preQueryBehaviorSummary.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                      {[...preQueryBehaviorSummary].sort((a, b) => a.earliestStart - b.earliestStart).map((item, index) => {
                        const percentage = Math.round((item.totalDuration / (preQueryTimeRange.endTime - preQueryTimeRange.startTime)) * 100);
                        return (
                          <Box 
                            key={index}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              p: 0.75,
                              bgcolor: `${item.behavior.color}10`,
                              borderRadius: 1,
                              border: `1px solid ${item.behavior.color}30`
                            }}
                          >
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.behavior.color }} />
                            <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }}>
                              {item.behavior.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                              {percentage}%
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Paper>

                {preQueryBehaviorSummary.length === 0 && (
                  <Box sx={{ p: 1.5, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fcd34d' }}>
                    <Typography variant="caption" sx={{ color: '#92400e' }}>
                      No behavioral tags in pre-query region. Label behaviors in the Labeling tab first.
                    </Typography>
                  </Box>
                )}

                <TextField
                  label="Pattern Name"
                  fullWidth
                  size="small"
                  value={preQueryPatternName}
                  onChange={(e) => setPreQueryPatternName(e.target.value)}
                  placeholder="e.g., Initial Exploration, Setup Phase"
                />

                <TextField
                  label="Description"
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={preQueryPatternDescription}
                  onChange={(e) => setPreQueryPatternDescription(e.target.value)}
                  placeholder="Describe what the student did before seeking help..."
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    onClick={handleSavePreQueryPattern} 
                    disabled={!preQueryPatternName.trim()} 
                    sx={{ flex: 1 }}
                  >
                    {existingPreQueryPattern ? 'Update Pattern' : 'Save Pattern'}
                  </Button>
                </Box>

                {showPreQuerySaved && (
                  <Typography variant="caption" sx={{ color: '#22c55e', textAlign: 'center' }}>
                    ✓ Pre-query pattern saved
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af', border: '2px dashed #e5e7eb', borderRadius: 2 }}>
                <Timeline sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">
                  {firstSeekingHelpTime === null 
                    ? "No 'Seeking Help' tags found. Label behaviors in the Labeling tab first."
                    : "The student's first help-seeking was at the very start of the session."}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Tab 2: LLM Queries */}
      {activeTab === 2 && (
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column', pb: '80px' }}>
          {/* Header with count */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb', bgcolor: '#f9fafb', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" fontWeight="700" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Student Queries ({chatQueries.length})
              </Typography>
              <Chip 
                size="small" 
                label={`${labeledQueryCount}/${chatQueries.length} labeled`}
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem',
                  bgcolor: labeledQueryCount === chatQueries.length && chatQueries.length > 0 ? '#dcfce7' : '#f3f4f6',
                  color: labeledQueryCount === chatQueries.length && chatQueries.length > 0 ? '#166534' : '#6b7280'
                }}
              />
            </Box>
          </Box>

          {/* Query List */}
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', p: 1.5 }}>
            {chatQueries.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af', border: '2px dashed #e5e7eb', borderRadius: 2 }}>
                <Chat sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">No LLM queries found in this session</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {chatQueries.map((query, index) => {
                  const isSelected = selectedQueryId === query.id;
                  const currentLabel = queryLabels[query.id];
                  const labelInfo = QUERY_INTENT_LABELS.find(l => l.id === currentLabel);
                  
                  return (
                    <Paper
                      key={query.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: 2,
                        bgcolor: isSelected ? '#eff6ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: isSelected ? '#dbeafe' : '#f9fafb', borderColor: isSelected ? '#3b82f6' : '#d1d5db' }
                      }}
                      onClick={() => handleQueryClick(query)}
                    >
                      {/* Query header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            size="small" 
                            label={`#${index + 1}`}
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#3b82f6', color: '#fff' }}
                          />
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {formatTime(query.time)}
                          </Typography>
                        </Box>
                        {currentLabel && (
                          <Chip 
                            size="small" 
                            label={labelInfo?.label || currentLabel}
                            sx={{ 
                              height: 20, 
                              fontSize: '0.65rem',
                              bgcolor: '#dbeafe',
                              color: '#1d4ed8'
                            }}
                          />
                        )}
                      </Box>

                      {/* Query text preview */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#374151', 
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.8rem',
                          lineHeight: 1.4
                        }}
                      >
                        {query.text || query.payload?.query || 'No query text'}
                      </Typography>

                      {/* Label dropdown */}
                      <FormControl fullWidth size="small">
                        <Select
                          value={currentLabel || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleQueryLabelChange(query.id, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          displayEmpty
                          sx={{
                            fontSize: '0.75rem',
                            bgcolor: '#f9fafb',
                            '& .MuiSelect-select': { py: 1 }
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select query intent...</em>
                          </MenuItem>
                          {QUERY_INTENT_LABELS.map((label) => (
                            <MenuItem key={label.id} value={label.id}>
                              <Box>
                                <Typography variant="body2" fontWeight="500">{label.label}</Typography>
                                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                                  {label.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Paper>
                  );
                })}
              </Box>
            )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab 3: Profiles */}
      {activeTab === 3 && (
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb', bgcolor: '#f9fafb', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" fontWeight="700" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Behavioral Profiles ({queryWindows.length})
              </Typography>
              <Chip 
                size="small" 
                label={`${labeledProfileCount}/${queryWindows.length} labeled`}
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem',
                  bgcolor: labeledProfileCount === queryWindows.length && queryWindows.length > 0 ? '#dcfce7' : '#f3f4f6',
                  color: labeledProfileCount === queryWindows.length && queryWindows.length > 0 ? '#166534' : '#6b7280'
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
              Classify the student's behavioral profile in each pre-query window.
            </Typography>
          </Box>

          {/* Windows List */}
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', p: 1.5 }}>
              {queryWindows.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af', border: '2px dashed #e5e7eb', borderRadius: 2 }}>
                  <AccountTree sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2">No queries found. Label behaviors and queries first.</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {queryWindows.map((window) => {
                    const isSelected = selectedProfileQueryId === window.queryId;
                    const currentProfile = profileLabels[window.queryId];
                    const profileInfo = window.profiles.find(p => p.id === currentProfile);
                    const isW1 = window.windowType === 'W1';

                    return (
                      <Paper
                        key={`${window.queryId}-${window.windowType}`}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          border: isSelected ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                          borderRadius: 2,
                          bgcolor: isSelected ? '#f5f3ff' : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': { bgcolor: isSelected ? '#ede9fe' : '#f9fafb', borderColor: isSelected ? '#8b5cf6' : '#d1d5db' }
                        }}
                        onClick={() => handleProfileWindowClick(window)}
                      >
                        {/* Window header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              size="small" 
                              label={isW1 ? 'W1' : 'W2'}
                              sx={{ 
                                height: 22, 
                                fontSize: '0.7rem', 
                                fontWeight: 700,
                                bgcolor: isW1 ? '#dbeafe' : '#fef3c7', 
                                color: isW1 ? '#1d4ed8' : '#92400e',
                                border: isW1 ? '1px solid #93c5fd' : '1px solid #fcd34d'
                              }}
                            />
                            <Chip 
                              size="small" 
                              label={`Q${window.queryIndex + 1}`}
                              sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f3f4f6', color: '#6b7280' }}
                            />
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              {formatTime(window.windowStart)} → {formatTime(window.windowEnd)}
                            </Typography>
                          </Box>
                          {currentProfile && (
                            <Chip 
                              size="small" 
                              label={profileInfo?.label || currentProfile}
                              sx={{ 
                                height: 20, 
                                fontSize: '0.65rem',
                                bgcolor: profileInfo?.color ? `${profileInfo.color}20` : '#f3f4f6',
                                color: profileInfo?.color || '#6b7280',
                                border: `1px solid ${profileInfo?.color || '#e5e7eb'}`,
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>

                        {/* Window type label */}
                        <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 1, fontSize: '0.65rem' }}>
                          {isW1 ? 'Before first query' : `Between query ${window.queryIndex} and ${window.queryIndex + 1}`}
                          {' · '}
                          {formatTime(window.windowDuration)} duration
                        </Typography>

                        {/* Behavioral sequence bar */}
                        {renderWindowSequenceBar(window.behaviors, window.windowDuration)}

                        {/* Behavior summary chips */}
                        {window.behaviorSummary.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {window.behaviorSummary.map((item, idx) => {
                              const pct = window.windowDuration > 0 
                                ? Math.round((item.totalDuration / window.windowDuration) * 100) 
                                : 0;
                              return (
                                <Chip
                                  key={idx}
                                  size="small"
                                  label={`${item.behavior.label} ${pct}%`}
                                  sx={{
                                    height: 18,
                                    fontSize: '0.6rem',
                                    bgcolor: `${item.behavior.color}15`,
                                    color: item.behavior.color,
                                    border: `1px solid ${item.behavior.color}40`,
                                    fontWeight: 500
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}

                        {/* Query text context */}
                        <Box sx={{ mt: 1, p: 1, bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                          <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 0.25, fontSize: '0.6rem', textTransform: 'uppercase' }}>
                            Query sent
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: '#374151', 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontSize: '0.75rem',
                            lineHeight: 1.3,
                            fontStyle: 'italic'
                          }}>
                            "{window.queryText}"
                          </Typography>
                        </Box>

                        {/* Profile dropdown */}
                        <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                          <Select
                            value={currentProfile || ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleProfileLabelChange(window.queryId, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            displayEmpty
                            sx={{
                              fontSize: '0.75rem',
                              bgcolor: '#f9fafb',
                              '& .MuiSelect-select': { py: 1 }
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>{isW1 ? 'Select W1 profile...' : 'Select W2 profile...'}</em>
                            </MenuItem>
                            {window.profiles.map((profile) => (
                              <MenuItem key={profile.id} value={profile.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: profile.color, flexShrink: 0 }} />
                                  <Box>
                                    <Typography variant="body2" fontWeight="600">{profile.label}</Typography>
                                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                                      {profile.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Tab 4: Assessment */}
      {activeTab === 4 && (
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {savedPatterns && savedPatterns.length > 0 && (
            <Box sx={{ p: 1.5, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
              <Typography variant="caption" fontWeight="700" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                Patterns ({savedPatterns.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto' }}>
                {savedPatterns.map((pattern) => {
                  const isSelected = editingPatternId === pattern.id;
                  return (
                    <Paper 
                      key={pattern.id} 
                      elevation={0}
                      sx={{ 
                        p: 1, 
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb', 
                        borderRadius: 1.5,
                        bgcolor: isSelected ? '#eff6ff' : '#fff',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: isSelected ? '#dbeafe' : '#f9fafb' }
                      }}
                      onClick={() => handleLoadPattern(pattern)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight="600" noWrap sx={{ color: isSelected ? '#1d4ed8' : '#374151' }}>
                            {pattern.name}
                          </Typography>
                          {pattern.isPreQuery && (
                            <Chip 
                              label="Pre-Query" 
                              size="small" 
                              sx={{ 
                                height: 18, 
                                fontSize: '0.65rem',
                                bgcolor: '#dbeafe', 
                                color: '#1d4ed8' 
                              }} 
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            size="small"
                            label={pattern.assessment?.isComplete ? '✓' : '○'}
                            sx={{
                              height: 20,
                              bgcolor: pattern.assessment?.isComplete ? '#dcfce7' : '#f3f4f6',
                              color: pattern.assessment?.isComplete ? '#166534' : '#9ca3af',
                            }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); onDeletePattern(pattern.id); }}
                            sx={{ p: 0.5, color: '#9ca3af', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          )}

          <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
            {timelineSelection ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ pb: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                  <Typography variant="overline" sx={{ color: '#6b7280' }}>Assessing</Typography>
                  <Typography variant="h6" fontWeight="600">{patternName || 'Unnamed pattern'}</Typography>
                  
                  {behaviorsInSelection.length > 0 && timelineSelection && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" fontWeight="600" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                        Behavioral Sequence
                      </Typography>
                      <Box sx={{ 
                        position: 'relative',
                        height: 24, 
                        borderRadius: 1.5, 
                        overflow: 'hidden',
                        bgcolor: '#e5e7eb',
                        border: '1px solid #d1d5db'
                      }}>
                        {[...behaviorsInSelection].sort((a, b) => a.overlapStart - b.overlapStart).map((block, index) => {
                          const selStart = timelineSelection.startTime;
                          const selDuration = timelineSelection.endTime - selStart;
                          if (selDuration <= 0) return null;
                          const left = ((block.overlapStart - selStart) / selDuration) * 100;
                          const width = ((block.overlapEnd - block.overlapStart) / selDuration) * 100;
                          return (
                            <Tooltip 
                              key={block.id || index}
                              title={`${block.behavior.label}: ${formatTime(block.overlapEnd - block.overlapStart)}`}
                              arrow
                            >
                              <Box sx={{
                                position: 'absolute',
                                left: `${left}%`,
                                width: `${Math.max(width, 0.5)}%`,
                                height: '100%',
                                bgcolor: block.behavior.color,
                                opacity: 0.85,
                                zIndex: 1
                              }} />
                            </Tooltip>
                          );
                        })}

                        {preQuerySubSelection && (() => {
                          const selStart = timelineSelection.startTime;
                          const selEnd = timelineSelection.endTime;
                          const selDuration = selEnd - selStart;
                          if (selDuration <= 0) return null;
                          
                          const hlStart = Math.max(preQuerySubSelection.startTime, selStart);
                          const hlEnd = Math.min(preQuerySubSelection.endTime, selEnd);
                          if (hlEnd <= hlStart) return null;
                          
                          const left = ((hlStart - selStart) / selDuration) * 100;
                          const width = ((hlEnd - hlStart) / selDuration) * 100;
                          
                          return (
                            <Box sx={{
                              position: 'absolute',
                              top: 0,
                              left: `${left}%`,
                              width: `${width}%`,
                              height: '100%',
                              bgcolor: '#f59e0b',
                              opacity: 0.6,
                              zIndex: 3,
                              pointerEvents: 'none',
                              border: '2px solid #fbbf24',
                              boxSizing: 'border-box',
                              borderRadius: 1
                            }} />
                          );
                        })()}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>
                          {formatTime(timelineSelection.startTime)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem' }}>
                          {formatTime(timelineSelection.endTime)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {renderAssessmentForm(
                  productivity, setProductivity,
                  interventionRecommendation, setInterventionRecommendation,
                  annotatorConfidence, setAnnotatorConfidence
                )}

                <Button
                  variant="contained"
                  onClick={handleSavePattern}
                  disabled={!isAssessmentComplete}
                  sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
                >
                  Save Assessment
                </Button>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8, color: '#9ca3af' }}>
                <Timeline sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }} />
                <Typography variant="body2">{savedPatterns && savedPatterns.length > 0 ? 'Select a pattern to assess' : 'Create patterns first'}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Add Label Dialog */}
      <Dialog open={addLabelDialogOpen} onClose={handleAddLabelCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Add Custom Behavioral Label</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Label Name"
              fullWidth
              size="small"
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={newLabelDescription}
              onChange={(e) => setNewLabelDescription(e.target.value)}
            />
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Color</Typography>
              <input
                type="color"
                value={newLabelColor}
                onChange={(e) => setNewLabelColor(e.target.value)}
                style={{ width: '100%', height: 40, border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddLabelCancel}>Cancel</Button>
          <Button onClick={handleAddLabelConfirm} variant="contained" disabled={!newLabelName.trim()}>Add Label</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}